import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Plug,
  PlugsConnected,
  CircleNotch,
  ArrowsClockwise,
  ArrowSquareOut,
  CheckCircle,
  Warning,
} from "@phosphor-icons/react";
import { api } from "../lib/api";

/**
 * Admin-only Upstox connection panel.
 *
 * Displays current token status (connected / expired / missing) and offers
 * two reconnect actions:
 *   - "Auto-refresh"  → POST /upstox/auth/auto-refresh  (TOTP-based)
 *   - "OAuth login"   → GET  /upstox/auth/start         (manual login)
 *
 * Token expiry timestamp is shown so the admin can plan ahead. Polls every
 * 60s while panel is mounted.
 */
export default function AdminUpstoxPanel() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // 'auto' | 'oauth' | 'health' | null
  const [msg, setMsg] = useState("");
  const [health, setHealth] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/upstox/status");
      setStatus(data);
    } catch (e) {
      console.error("upstox status", e);
      setStatus({ has_token: false, error: e?.response?.data?.detail });
    } finally {
      setLoading(false);
    }
  }, []);

  const checkHealth = async () => {
    setBusy("health");
    try {
      const { data } = await api.get("/admin/data-source/health");
      setHealth(data);
    } catch (e) {
      console.error("data-source health", e);
      setHealth({ error: e?.response?.data?.detail || "Health check failed" });
    } finally {
      setBusy(null);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, [load]);

  const autoRefresh = async () => {
    setBusy("auto");
    setMsg("");
    try {
      const { data } = await api.post("/upstox/auth/auto-refresh");
      setMsg(data?.message || "Auto-refresh succeeded · token rotated.");
      await load();
    } catch (e) {
      console.error("auto-refresh failed", e);
      const detail =
        e?.response?.data?.detail || e?.message || "Auto-refresh failed";
      setMsg(
        typeof detail === "string"
          ? detail
          : detail?.message || JSON.stringify(detail),
      );
    } finally {
      setBusy(null);
    }
  };

  const oauthLogin = async () => {
    setBusy("oauth");
    setMsg("");
    try {
      const { data } = await api.get("/upstox/auth/start");
      if (data?.authorize_url) {
        // Open Upstox login in new tab. After user signs in, Upstox redirects
        // back to /api/upstox/auth/callback which persists the token, so when
        // they come back to this tab and we poll /status it'll show connected.
        window.open(data.authorize_url, "_blank", "noopener,noreferrer");
        setMsg(
          "Upstox login opened in a new tab. Sign in there → it will auto-redirect and persist the token. Come back here in 1 min and check status.",
        );
      } else {
        setMsg("No authorize URL returned. Check Upstox config in backend .env.");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "OAuth start failed";
      setMsg(typeof detail === "string" ? detail : JSON.stringify(detail));
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="px-6 py-4 bg-surface/40 border-y border-subtle text-[10px] font-mono uppercase tracking-widest text-muted-fg flex items-center gap-2">
        <CircleNotch size={12} weight="bold" className="animate-spin" />
        Loading Upstox status…
      </div>
    );
  }

  const connected = !!status?.has_token;
  const expiresAt = status?.expires_at
    ? new Date(status.expires_at).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
    : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      className={
        "px-6 py-4 bg-surface/40 border-y " +
        (connected ? "border-pos/30" : "border-mod/40")
      }
      data-testid="admin-upstox-panel"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            {connected ? (
              <PlugsConnected size={14} weight="fill" className="text-pos" />
            ) : (
              <Plug size={14} weight="fill" className="text-mod" />
            )}
            <div className="font-mono text-xs uppercase tracking-[0.22em] text-primary-fg">
              Upstox Connection
            </div>
            <span
              className={
                "px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest border rounded-sm flex items-center gap-1 " +
                (connected
                  ? "border-pos text-pos bg-pos-soft"
                  : "border-mod text-mod bg-mod-soft")
              }
              data-testid="upstox-status-pill"
            >
              {connected ? (
                <>
                  <CheckCircle size={9} weight="fill" /> Connected
                </>
              ) : (
                <>
                  <Warning size={9} weight="fill" /> Disconnected
                </>
              )}
            </span>
          </div>
          <div className="text-xs text-muted-fg">
            {connected ? (
              <>
                Live LTPs flowing for SL/Target hits + F&O OI. Token expires{" "}
                <span className="text-secondary-fg">{expiresAt || "—"}</span>.
              </>
            ) : (
              <>
                No active token. Live SL/Target hit detection + F&O OI updates
                are paused. Reconnect below to restore.
              </>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={load}
            data-testid="upstox-refresh-status"
            className="p-1.5 rounded-sm border border-subtle text-muted-fg hover:text-primary-fg hover:border-pos/40 transition"
            title="Refresh status"
          >
            <ArrowsClockwise size={12} weight="bold" />
          </button>
          <button
            type="button"
            onClick={autoRefresh}
            disabled={busy === "auto"}
            data-testid="upstox-auto-refresh-btn"
            className="px-3 py-1.5 border border-pos/40 bg-pos-soft text-pos rounded-sm text-[10px] font-mono uppercase tracking-widest hover:opacity-90 transition flex items-center gap-1.5 disabled:opacity-50"
            title="TOTP-based auto-login (requires UPSTOX_TOTP_SECRET in env)"
          >
            {busy === "auto" ? (
              <>
                <CircleNotch size={11} weight="bold" className="animate-spin" />
                Refreshing…
              </>
            ) : (
              <>
                <ArrowsClockwise size={11} weight="bold" />
                Auto-refresh
              </>
            )}
          </button>
          <button
            type="button"
            onClick={oauthLogin}
            disabled={busy === "oauth"}
            data-testid="upstox-oauth-btn"
            className="px-3 py-1.5 border border-mod/40 bg-mod-soft text-mod rounded-sm text-[10px] font-mono uppercase tracking-widest hover:opacity-90 transition flex items-center gap-1.5 disabled:opacity-50"
            title="Manual OAuth login — opens Upstox login page in a new tab"
          >
            {busy === "oauth" ? (
              <>
                <CircleNotch size={11} weight="bold" className="animate-spin" />
                Starting…
              </>
            ) : (
              <>
                <ArrowSquareOut size={11} weight="bold" />
                OAuth Login
              </>
            )}
          </button>
        </div>
      </div>

      {msg && (
        <div
          className={
            "mt-3 px-3 py-2 text-xs font-mono rounded-sm border " +
            (connected
              ? "border-pos/40 bg-pos-soft text-pos"
              : "border-mod/40 bg-mod-soft text-mod")
          }
          data-testid="upstox-msg"
        >
          {msg}
        </div>
      )}

      {/* === Data-source health probe === */}
      <div className="mt-4 pt-4 border-t border-subtle">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-fg">
            Data sources · yfinance + Upstox
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={async () => {
                setBusy("test");
                try {
                  await api.post("/admin/data-source/test-alert");
                  setMsg("Test alert sent · check email + Telegram channel");
                } catch (e) {
                  setMsg(e?.response?.data?.detail || "Test alert failed");
                } finally { setBusy(null); }
              }}
              disabled={busy === "test"}
              data-testid="data-source-test-alert-btn"
              className="px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest rounded-sm border border-subtle text-secondary-fg hover:text-primary-fg hover:border-mod/40 disabled:opacity-50"
              title="Send a test alert to verify email + Telegram wiring"
            >
              {busy === "test" ? "Sending…" : "Test alert"}
            </button>
            <button
              type="button"
              onClick={checkHealth}
              disabled={busy === "health"}
              data-testid="data-source-health-btn"
              className="px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest rounded-sm border border-subtle text-secondary-fg hover:text-primary-fg hover:border-pos/40 disabled:opacity-50"
            >
              {busy === "health" ? (
                <span className="inline-flex items-center gap-1">
                  <CircleNotch size={10} weight="bold" className="animate-spin" />
                  Probing…
                </span>
              ) : (
                "Probe health"
              )}
            </button>
          </div>
        </div>
        {health && !health.error && (
          <div className="grid grid-cols-2 gap-2 text-xs font-mono" data-testid="data-source-health-result">
            <div className={"px-3 py-2 rounded-sm border " + (health.yfinance?.ok ? "border-pos/40 bg-pos-soft/30 text-pos" : "border-neg/40 bg-neg-soft/30 text-neg")}>
              <div className="text-[9px] uppercase tracking-widest">yfinance</div>
              <div className="font-mono text-sm">
                {health.yfinance?.ok ? "OK" : "DOWN"}
              </div>
              <div className="text-[9px] normal-case text-secondary-fg">
                {health.yfinance?.rows || 0} rows · {health.yfinance?.latency_ms || 0} ms
              </div>
            </div>
            <div className={"px-3 py-2 rounded-sm border " + (health.upstox?.ok ? "border-pos/40 bg-pos-soft/30 text-pos" : "border-neg/40 bg-neg-soft/30 text-neg")}>
              <div className="text-[9px] uppercase tracking-widest">upstox</div>
              <div className="font-mono text-sm">
                {health.upstox?.ok ? "OK" : "DOWN"}
              </div>
              <div className="text-[9px] normal-case text-secondary-fg">
                {health.upstox?.rows || 0} rows · {health.upstox?.latency_ms || 0} ms
              </div>
            </div>
            <div className="col-span-2 text-[10px] text-muted-fg font-mono normal-case">
              Status: <span className={health.primary === "yfinance" ? "text-pos" : health.primary === "upstox" ? "text-mod" : "text-neg"}>{health.status}</span> · Primary: {health.primary || "none"}
            </div>
          </div>
        )}
        {health?.error && (
          <div className="px-3 py-2 text-xs font-mono rounded-sm border border-neg/40 bg-neg-soft/30 text-neg">
            {health.error}
          </div>
        )}
      </div>
    </motion.div>
  );
}
